import { useEffect, useState } from "react";
import {
  FaArrowAltCircleDown,
  FaGithub,
  FaLinkedin,
  FaEnvelope,
  FaFileDownload,
  FaEye
} from "react-icons/fa";
import Typewriter from "typewriter-effect";
import TechStack from "../components/TechStack";
import Projects from "../components/Projects";
import Achievements from "../components/Achievements";

export default function Home() {
  const [showDiv, setShowDiv] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowDiv(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const sections = ["home", "tech-stack", "projects", "achievements", "contact"];

  const scrollToProjects = () => {
    document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToNextSection = () => {
    const currentSectionIndex = sections.findIndex((id) => {
      const section = document.getElementById(id);
      if (!section) return false;
      return section.getBoundingClientRect().top >= 0;
    });

    if (currentSectionIndex !== -1 && currentSectionIndex < sections.length - 1) {
      document
        .getElementById(sections[currentSectionIndex + 1])
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-black text-white relative">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black bg-opacity-90 backdrop-blur-lg p-4 border-b border-gray-800">
        <div className="flex flex-wrap justify-center gap-4 md:gap-12 px-4">
          {["Home", "Tech Stack", "Projects", "Achievements", "Contact"].map((item, index) => (
            <button
              key={item}
              onClick={() => scrollToSection(sections[index])}
              className="text-white hover:text-blue-400 transition-all duration-300 font-medium text-sm md:text-lg relative group whitespace-nowrap"
            >
              {item}
              <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-blue-400 transition-all duration-300 group-hover:w-full"></span>
            </button>
          ))}
        </div>
      </nav>

      {/* Hero Section – widened container */}
      <header
        id="home"
        className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black text-white pt-20 md:pt-0 px-4"
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        </div>

        {/* Hero content – wider max width */}
        <div className="flex flex-col md:flex-row-reverse items-center justify-between w-full max-w-[1400px] mx-auto gap-8 md:gap-12 px-4 md:px-6">
          {/* Profile Image */}
          <div className="md:w-1/2 flex justify-center z-10 order-1 md:order-2">
            <div
              className={`w-40 h-40 md:w-80 md:h-80 transform ease-in-out duration-1000 transition-all ${showDiv ? "opacity-0 scale-95" : "opacity-100 scale-100"
                } relative`}
            >
              {!showDiv && (
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full blur-lg opacity-30 group-hover:opacity-50 transition duration-1000"></div>
                  <img
                    src="/images/profile.png"
                    alt="Profile"
                    className="rounded-full shadow-2xl relative z-10 transform group-hover:scale-105 transition-transform duration-500 border-4 border-gray-800 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 rounded-full border-2 border-transparent bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
                </div>
              )}
            </div>
          </div>

          {/* Text Section */}
          <div className="text-center md:text-left md:w-1/2 z-10 order-2 md:order-1">
            <div className="mb-6">
              <h1 className="text-3xl md:text-6xl font-bold tracking-tight fadein-text transition-opacity duration-1000 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Vinod Pachipala
              </h1>
              <div className="text-lg md:text-2xl font-semibold fadein-text mt-4 h-8">
                <Typewriter
                  options={{
                    strings: ["Web Developer", "PERN Stack Engineer", "Full-Stack Developer"],
                    autoStart: true,
                    loop: true,
                    deleteSpeed: 50,
                    delay: 70,
                  }}
                />
              </div>
            </div>

            <p
              className={`transform transition-all ease-in-out duration-2000 text-base md:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto md:mx-0 ${showDiv ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
                }`}
            >
              Passionate full-stack web developer with expertise in React.js,
              Node.js, Express.js, and PostgreSQL.
            </p>

            {/* Buttons */}
            <div
              className={`mt-6 transition-all duration-2000 flex flex-row gap-4 justify-center md:justify-start ${showDiv ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
                }`}
            >
              <a
                href="https://drive.google.com/file/d/1eoPQe0R3ADr0p8tIqxxj1wsFqfzVse0j/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 px-5 py-2.5 md:px-8 md:py-3 rounded-xl font-medium text-sm md:text-base text-white overflow-hidden transition-all duration-300 transform hover:scale-105 focus:outline-none"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 rounded-xl"></span>
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 rounded-xl blur-md"></span>
                <span className="absolute inset-[2px] bg-black rounded-xl group-hover:bg-gray-900 transition-colors duration-300"></span>
                <span className="relative z-10 flex items-center gap-2">
            
                  View Resume
                </span>
              </a>

              <button
                onClick={scrollToProjects}
                className="group relative inline-flex items-center gap-2 px-5 py-2.5 md:px-8 md:py-3 rounded-xl font-medium text-sm md:text-base text-white overflow-hidden transition-all duration-300 transform hover:scale-105 focus:outline-none"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-green-600 rounded-xl"></span>
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-lime-500 via-yellow-500 to-amber-500 rounded-xl blur-md"></span>
                <span className="absolute inset-[2px] bg-black rounded-xl group-hover:bg-gray-900 transition-colors duration-300"></span>
                <span className="relative z-10 flex items-center gap-2">
                 
                  View Projects
                </span>
              </button>
            </div>

            {/* Social Links */}
            <div
              className={`transition-all duration-2000 mt-6 ${showDiv ? "opacity-0" : "opacity-100"
                } flex gap-4 justify-center md:justify-start`}
            >
              {[
                { icon: FaGithub, href: "https://github.com/vinodpachipala1" },
                { icon: FaLinkedin, href: "https://www.linkedin.com/in/vinod-pachipala-891375372/" },
                { icon: FaEnvelope, href: "mailto:vinodpachipala93@gmail.com" }
              ].map(({ icon: Icon, href }, index) => (
                <a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-800 hover:bg-gray-700 p-2 md:p-3 rounded-full transition-all duration-300 transform hover:scale-110 hover:shadow-lg border border-gray-700"
                >
                  <Icon size={20} className="md:size-6 hover:text-blue-400 transition-colors duration-300" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Tech Stack Section – wider container */}
      <section id="tech-stack" className="py-10 md:py-14 px-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <TechStack />
        </div>
      </section>

      {/* Projects Section – wider container */}
      <section id="projects" className="py-10 md:py-14 px-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Projects />
        </div>
      </section>

      {/* Achievements Section – wider container */}
      <section id="achievements" className="py-10 md:py-14 px-4">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Achievements />
        </div>
      </section>

      {/* Contact Section – widened from max-w-4xl to max-w-6xl */}
      <section id="contact" className="py-12 md:py-16 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto text-center px-4 md:px-6">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            Get In Touch
          </h2>
          <p className="text-base md:text-xl text-gray-300 mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed">
            Feel free to reach out via email or connect with me on social media.
            I'm always open to discussing new opportunities and interesting projects.
          </p>
          <div className="flex justify-center space-x-4 md:space-x-8">
            {[
              { icon: FaGithub, href: "https://github.com/vinodpachipala1", label: "GitHub" },
              { icon: FaLinkedin, href: "https://www.linkedin.com/in/vinod-pachipala-891375372/", label: "LinkedIn" },
              { icon: FaEnvelope, href: "mailto:vinodpachipala93@gmail.com", label: "Email" }
            ].map(({ icon: Icon, href, label }, index) => (
              <a key={index} href={href} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center group">
                <div className="bg-gray-800 hover:bg-gray-700 p-3 md:p-4 rounded-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-xl border border-gray-700 mb-2 md:mb-3">
                  <Icon size={20} className="md:size-7 hover:text-blue-400 transition-colors duration-300" />
                </div>
                <span className="text-gray-400 group-hover:text-white transition-colors duration-300 text-xs md:text-sm font-medium">{label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Down Arrow – unchanged, already unique */}
      <button
        onClick={scrollToNextSection}
        className="fixed bottom-6 right-6 z-40 group focus:outline-none"
        aria-label="Scroll to Next Section"
      >
        <div className="relative">
          <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-75 group-hover:opacity-100 blur-md transition duration-300 animate-pulse"></div>
          <div className="relative bg-black/40 backdrop-blur-md rounded-full p-3 md:p-4 border border-white/30 group-hover:border-indigo-400 transition-all duration-300 transform group-hover:scale-110 shadow-xl">
            <FaArrowAltCircleDown
              size={20}
              className="md:size-7 text-white group-hover:text-indigo-400 transition-all duration-300"
            />
          </div>
        </div>
      </button>
    </div>
  );
}